import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Check, X, Pencil, AlertCircle } from "lucide-react";

export const REVIEW_LOW_CONFIDENCE = 0.6;

/**
 * An item in a review-and-correct list (snap-a-meal photo results or
 * barcode-scanned products). Carries the original AI/lookup name + grams so we
 * can detect user edits and route them into the corrections store after logging.
 */
export type EditableReviewItem = {
  foodName: string;
  grams: number;
  category: string;
  confidence: number;
  description: string;
  aiFoodName: string;
  aiGrams: number;
};

export function FoodReviewRow({
  item,
  onName,
  onGrams,
  onRemove,
  disabled,
}: {
  item: EditableReviewItem;
  onName: (name: string) => void;
  onGrams: (grams: number) => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.foodName);
  const inputRef = useRef<HTMLInputElement>(null);

  const low = item.confidence > 0 && item.confidence < REVIEW_LOW_CONFIDENCE;
  const pct = item.confidence > 0 ? Math.round(item.confidence * 100) : null;

  const startEdit = () => {
    setDraft(item.foodName);
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 10);
  };
  const saveName = () => {
    const next = draft.trim();
    if (next) onName(next);
    setEditing(false);
  };
  const cancelName = () => {
    setEditing(false);
    setDraft(item.foodName);
  };

  return (
    <div className={`p-2.5 rounded-lg bg-muted/30 border ${low ? "border-amber-500/40" : "border-border"}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-1">
              <input
                ref={inputRef}
                type="text"
                value={draft}
                disabled={disabled}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveName();
                  else if (e.key === "Escape") cancelName();
                }}
                autoFocus
                aria-label={t("quickLog.editFoodName")}
                className="flex-1 min-w-0 text-sm px-2 py-1 rounded bg-background/60 border border-border text-foreground focus:outline-none focus:border-primary/50"
              />
              <button
                type="button"
                onClick={saveName}
                className="text-primary hover:text-primary/80 transition-colors"
                aria-label={t("common.save")}
              >
                <Check className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={cancelName}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label={t("common.cancel")}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={startEdit}
              disabled={disabled}
              className="flex items-center gap-1.5 group/name text-left"
              aria-label={t("quickLog.editFoodName")}
            >
              <span className="text-sm font-medium text-foreground truncate">{item.foodName}</span>
              <Pencil className="w-3 h-3 text-muted-foreground/40 group-hover/name:text-muted-foreground/80 transition-colors shrink-0" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {pct != null && (
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                low
                  ? "bg-amber-500/15 text-amber-400 border border-amber-500/25"
                  : "bg-primary/10 text-primary/80 border border-primary/20"
              }`}
            >
              {t("quickLog.confidenceLabel", { percent: pct })}
            </span>
          )}
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            title={t("plateTalk.removeItem")}
            aria-label={t("plateTalk.removeItem")}
            className="w-5 h-5 rounded-full flex items-center justify-center text-muted-foreground/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {low && (
        <div className="flex items-center gap-1.5 mb-1.5 text-[10px] text-amber-400/90">
          <AlertCircle className="w-3 h-3 shrink-0" />
          <span>{t("plateTalk.lowConfidenceHint")}</span>
        </div>
      )}

      <div className="flex items-center gap-1" title={t("plateTalk.adjustPortion")}>
        <button
          type="button"
          onClick={() => onGrams(item.grams - 10)}
          disabled={disabled || item.grams <= 1}
          aria-label={t("plateTalk.decrease")}
          className="w-6 h-6 rounded-md flex items-center justify-center bg-background/60 border border-border/30 text-muted-foreground hover:text-foreground hover:border-primary/40 disabled:opacity-40 transition-colors"
        >
          <span className="text-sm leading-none">−</span>
        </button>
        <div className="relative">
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={5000}
            value={item.grams}
            disabled={disabled}
            onChange={(e) => onGrams(parseInt(e.target.value, 10) || 0)}
            aria-label={t("plateTalk.adjustPortion")}
            className="w-16 h-6 text-center text-xs rounded-md bg-background/60 border border-border/30 text-foreground focus:outline-none focus:border-primary/50 pr-4 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground pointer-events-none">g</span>
        </div>
        <button
          type="button"
          onClick={() => onGrams(item.grams + 10)}
          disabled={disabled || item.grams >= 5000}
          aria-label={t("plateTalk.increase")}
          className="w-6 h-6 rounded-md flex items-center justify-center bg-background/60 border border-border/30 text-muted-foreground hover:text-foreground hover:border-primary/40 disabled:opacity-40 transition-colors"
        >
          <span className="text-sm leading-none">+</span>
        </button>
      </div>
    </div>
  );
}
